import {useAllRawTransactionsLazyQuery} from '@transactions/graphql';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import * as React from 'react';
import {
  InfoText,
  TransactionList,
  TransactionRow,
} from 'src/AccountLedgerView/TransactionSelectorComponents';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import Loading from 'src/Loading';
import {SearchInput} from 'src/SearchInput';
import type {
  AllRawTransactionsQueryVariables,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateFilterState} from 'src/components/DateFilterSelect';

export type RawTransactionSelection = {
  _id: string;
  amount: number; // cents
  // Unconsumed cents (null on legacy rows = fully unconsumed)
  remainingAmount: number | null;
  currency: string;
  accountId: string;
  date: string; // ISO date
};

const PAGE_SIZE = 20;

const formatAmount = (cents: number): string => {
  const abs = Math.abs(cents) / 100;
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${abs.toFixed(2)}`;
};

type Props = {
  accountId?: string;
  value: string; // raw transaction ID
  onChange: (
    rawTransactionId: string,
    transaction?: RawTransactionSelection,
  ) => void;
  /** Whether to include already-processed (fully consumed) transactions. Defaults to true. */
  includeProcessed?: boolean;
  /** Raw transaction IDs to exclude from results. */
  excludeIds?: string[];
};

export const RawTransactionPicker = (props: Props) => {
  const {accountId, value, onChange, includeProcessed = true, excludeIds} = props;

  const [search, setSearch] = React.useState('');
  const [dateFilter, setDateFilter] = React.useState<DateFilterState>({
    startDate: null,
    endDate: null,
  });

  // Pagination state
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [pageIndex, setPageIndex] = React.useState(0);

  const filterVariables = React.useMemo<
    Partial<AllRawTransactionsQueryVariables>
  >(
    () => ({
      ...(accountId ? {accountId} : {}),
      includeProcessed,
      limit: PAGE_SIZE,
      ...(search ? {search} : {}),
      ...(dateFilter.startDate ? {startDate: dateFilter.startDate} : {}),
      ...(dateFilter.endDate ? {endDate: dateFilter.endDate} : {}),
      ...(excludeIds && excludeIds.length > 0 ? {excludeIds} : {}),
    }),
    [accountId, includeProcessed, excludeIds, search, dateFilter.startDate, dateFilter.endDate],
  );

  const [loadTransactions, {data, loading}] =
    useAllRawTransactionsLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    });

  const pageData = data?.allRawTransactions;
  const transactions = pageData?.items ?? [];
  const hasNextPage = pageData?.hasMore ?? false;
  const hasPrevPage = pageIndex > 0;

  // Load first page when filters/search change
  React.useEffect(() => {
    setCursorStack([]);
    setPageIndex(0);
    void loadTransactions({variables: filterVariables});
  }, [filterVariables, loadTransactions]);

  const handleSearchSubmit = (query: string) => {
    const nextSearch = query.trim();
    if (nextSearch === search) {
      setCursorStack([]);
      setPageIndex(0);
      void loadTransactions({variables: filterVariables});
    } else {
      setSearch(nextSearch);
    }
  };

  const handleNextPage = () => {
    if (!hasNextPage || loading || !pageData?.endCursor) return;
    const nextCursor = pageData.endCursor;
    setCursorStack((prev) => {
      const next = [...prev];
      next[pageIndex] = nextCursor;
      return next;
    });
    setPageIndex((prev) => prev + 1);
    void loadTransactions({
      variables: {...filterVariables, beforeCursor: nextCursor},
    });
  };

  const handlePrevPage = () => {
    if (!hasPrevPage || loading) return;
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    if (prevIndex === 0) {
      void loadTransactions({variables: filterVariables});
    } else {
      void loadTransactions({
        variables: {
          ...filterVariables,
          beforeCursor: cursorStack[prevIndex - 1],
        },
      });
    }
  };

  const handleSelect = (tx: BasicRawTransactionFragment) => {
    onChange(tx._id, {
      _id: tx._id,
      amount: tx.amount,
      remainingAmount: tx.remainingAmount ?? null,
      currency: tx.currency,
      accountId: tx.account._id,
      date: tx.date,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <SearchInput
            inputProps={{placeholder: 'Search by description...'}}
            initialQuery=""
            onSearch={handleSearchSubmit}
          />
        </div>
        <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
      </div>

      <TransactionList>
        {loading ? (
          <Loading />
        ) : transactions.length === 0 ? (
          <InfoText>No transactions found</InfoText>
        ) : (
          transactions.map((tx) => (
            <TransactionRow key={tx._id}>
              <input
                type="radio"
                name="rawTransactionPicker"
                checked={value === tx._id}
                onChange={() => handleSelect(tx)}
                className="shrink-0"
              />
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm">{tx.description}</span>
                  <span className="text-muted-foreground text-xs">
                    {tx.date}
                  </span>
                </div>
                <span className="text-muted-foreground shrink-0 text-sm">
                  {formatAmount(tx.amount)}
                </span>
              </div>
            </TransactionRow>
          ))
        )}
      </TransactionList>

      {(hasPrevPage || hasNextPage) && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={!hasPrevPage || loading}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm disabled:opacity-50"
          >
            <ChevronLeft className="size-4" />
            Previous
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={!hasNextPage || loading}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm disabled:opacity-50"
          >
            Next
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
};
